import {
  capitalizeString,
  formatSalesOrderNumber,
  formatWorkOrderNumber,
} from '@zspace/format';
import { WorkOrderPermissions } from '@zspace/roles';
import { WorkOrderSummaryData } from '@zspace/types';
import { useMemo } from 'react';
import CheckPermissions from '../../shared/check-permissions/check-permissions';
import { formatDateToLocaleString } from '../../shared/utils';
import Link from '../../ui/link/link';
import NumericInput from '../../ui/numeric-input/numeric-input';
import PaginatedTable, {
  CustomPaginatedTableProps,
} from '../../ui/table/paginated-table/paginated-table';
import { Column } from '../../ui/table/types';
import ViewAllList from '../../ui/view-all-list/view-all-list';

export type WorkOrdersQueueTableProps =
  CustomPaginatedTableProps<WorkOrderSummaryData> & {
    includePriority?: boolean;
    onPriorityUpdate?: (workOrderId: string, priority: number) => void;
    isUpdatingPriority?: boolean;
  };

export function WorkOrdersQueueTable({
  dataSource,
  onChange,
  config,
  form,
  empty,
  includePriority = false,
  onPriorityUpdate,
  isUpdatingPriority = false,
  children,
}: WorkOrdersQueueTableProps) {
  const columns = useMemo(() => {
    const tableColumns: Column<WorkOrderSummaryData>[] = [
      {
        key: 'number',
        className: 'is-width-15',
        render: (el) => (
          <CheckPermissions
            permissions={[WorkOrderPermissions.WORK_ORDERS_DETAILS_READ]}
          >
            <CheckPermissions.Render>
              <Link to={`/work-orders/${el.id}`}>
                {formatWorkOrderNumber(el.number)}
              </Link>
            </CheckPermissions.Render>
            <CheckPermissions.Fallback>
              <span className="has-text-primary-dark">
                {formatWorkOrderNumber(el.number)}
              </span>
            </CheckPermissions.Fallback>
          </CheckPermissions>
        ),
        title: 'WO Number',
        sortable: false,
      },
      {
        key: 'name',
        className: 'is-width-15',
        render: (el) => (
          <CheckPermissions
            permissions={[WorkOrderPermissions.WORK_ORDERS_DETAILS_READ]}
          >
            <CheckPermissions.Render>
              <Link to={`/work-orders/${el.id}`}>{el.name}</Link>
            </CheckPermissions.Render>
            <CheckPermissions.Fallback>
              <span className="has-text-primary-dark">{el.name}</span>
            </CheckPermissions.Fallback>
          </CheckPermissions>
        ),
        title: 'WO Name',
        sortable: false,
      },
      {
        key: 'account',
        className: 'is-width-15',
        render: (el) => (
          <ViewAllList
            list={el.salesOrders
              .filter((so) => so.opportunity)
              .map((so) => so.opportunity?.account)}
            horizontalList
            renderItem={(account) => (
              <Link to={`/accounts/${account?.id}`}>{account?.name}</Link>
            )}
            separator=", "
          />
        ),
        title: 'Accounts',
        sortable: false,
      },
      {
        key: 'salesOrders',
        className: 'is-width-10',
        render: (el) => (
          <ViewAllList
            list={el.salesOrders}
            renderItem={(el) => <p>{formatSalesOrderNumber(el.number)}</p>}
          />
        ),
        title: 'Sales orders',
        sortable: false,
      },
      {
        key: 'type',
        className: 'is-width-10',
        render: (el) => capitalizeString(el.type).replace('_', ' '),
        title: 'Type',
        sortable: false,
      },
      {
        key: 'devices',
        className: includePriority ? 'is-width-5' : 'is-width-10',
        render: (el) => {
          return el.devices.assigned ?? 'N/A';
        },
        title: 'Devices',
      },
      {
        key: 'complex',
        className: 'is-width-5',
        render: (el) => (el.complex ? 'Yes' : 'No'),
        title: 'Complex',
        sortable: false,
      },
      {
        key: 'createdBy',
        className: 'is-width-15',
        dataIndex: 'createdBy',
        title: 'Created by',
        sortable: false,
      },
      {
        key: 'createdAt',
        className: 'is-width-5',
        render: (el) => formatDateToLocaleString(el.createdAt),
        title: 'Date',
        sortable: false,
      },
    ];

    const priorityColumn: Column<WorkOrderSummaryData> = {
      key: 'priority',
      className: 'is-width-5',
      title: 'Priority',
      render: (el) => (
        <CheckPermissions
          permissions={WorkOrderPermissions.WORK_ORDERS_QUEUE_PRIORITIZATION}
        >
          <CheckPermissions.Render>
            <NumericInput
              value={el.priority!}
              disabled={isUpdatingPriority}
              onChange={(priority: number) =>
                onPriorityUpdate?.(el.id, priority)
              }
            />
          </CheckPermissions.Render>
          <CheckPermissions.Fallback>
            <span className="pl-4 has-text-primary-dark">{el.priority}</span>
          </CheckPermissions.Fallback>
        </CheckPermissions>
      ),
      sortable: false,
    };
    return includePriority ? [priorityColumn, ...tableColumns] : tableColumns;
  }, [includePriority, isUpdatingPriority, onPriorityUpdate]);

  return (
    <PaginatedTable
      dataSource={dataSource}
      columns={columns}
      onChange={onChange}
      config={config}
      rowKey={'id'}
      form={form}
      empty={empty}
      children={children}
    />
  );
}

export default WorkOrdersQueueTable;
